import React from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { paths } from "../../../routes/paths";

// The LPO cycle (Batch199, 2026-09-30): submission -> approval -> goods received ->
// supplier invoice attached -> paid. Payment evidence differs by payment type: a CREDIT
// LPO is paid by cheque (attached on the LPO), a CASH LPO through the petty cash voucher
// its approval raised (found by description, shown with its status). Shared by the LPO
// list's Invoice / Payment columns and the detail page's cycle strip.

const fmt = (d) => (d ? dayjs(d).format("DD MMM YYYY") : "");
const received = (lpo) => ["PARTIALLY_RECEIVED", "FULLY_RECEIVED"].includes(lpo.status);
const closed = (lpo) => ["REJECTED", "CANCELLED"].includes(lpo.status);

/** Invoice cell: number + date, or what is still awaited. */
export const LpoInvoiceCell = ({ lpo }) => {
  if (lpo.hasInvoice) {
    return (
      <span className="text-nowrap">
        <span className="fw-semibold">{lpo.invoiceRef || "attached"}</span>
        {lpo.invoiceDate && <div className="small text-muted">{fmt(lpo.invoiceDate)}</div>}
      </span>
    );
  }
  if (closed(lpo)) return <span className="text-muted">-</span>;
  if (received(lpo)) return <span className="text-warning small">awaiting invoice</span>;
  return <span className="text-muted small">not yet received</span>;
};

/** Payment cell: cheque (credit) or petty cash voucher (cash). */
export const LpoPaymentCell = ({ lpo }) => {
  if (closed(lpo)) return <span className="text-muted">-</span>;
  if (lpo.paymentType === "CASH") {
    if (!lpo.pettyCashVoucherNumber) {
      return lpo.status === "APPROVED" || received(lpo)
        ? <span className="text-warning small">no petty cash voucher</span>
        : <span className="text-muted small">petty cash on approval</span>;
    }
    const paid = lpo.pettyCashVoucherStatus === "PAID";
    return (
      <span className="text-nowrap">
        <Link to={paths.pettyCashVoucherDetail(lpo.pettyCashVoucherRecordGuid)} className={paid ? "fw-semibold text-success" : "fw-semibold"}>
          {lpo.pettyCashVoucherNumber}
        </Link>
        <div className={`small ${paid ? "text-success" : "text-warning"}`}>{paid ? "paid - petty cash" : `voucher ${String(lpo.pettyCashVoucherStatus || "").toLowerCase()}`}</div>
      </span>
    );
  }
  if (lpo.hasCheque) {
    return (
      <span className="text-nowrap">
        <span className="fw-semibold text-success">Cheque {lpo.chequeRef || ""}</span>
        {lpo.chequeDate && <div className="small text-muted">{fmt(lpo.chequeDate)}</div>}
      </span>
    );
  }
  if (lpo.status === "FULLY_RECEIVED") return <span className="text-warning small">{lpo.hasInvoice ? "awaiting cheque" : "awaiting invoice"}</span>;
  return <span className="text-muted small">credit - after receipt</span>;
};

// Plain-text versions for the export.
export const lpoInvoiceText = (lpo) => (lpo.hasInvoice ? `${lpo.invoiceRef || "attached"}${lpo.invoiceDate ? ` (${fmt(lpo.invoiceDate)})` : ""}` : "");
export const lpoPaymentText = (lpo) => {
  if (lpo.paymentType === "CASH") return lpo.pettyCashVoucherNumber ? `${lpo.pettyCashVoucherNumber} ${lpo.pettyCashVoucherStatus || ""}`.trim() : "";
  return lpo.hasCheque ? `Cheque ${lpo.chequeRef || ""}${lpo.chequeDate ? ` (${fmt(lpo.chequeDate)})` : ""}`.trim() : "";
};

/** The five steps as a strip for the detail page. */
export const LpoCycleStrip = ({ lpo }) => {
  const isCash = lpo.paymentType === "CASH";
  const paid = isCash ? lpo.pettyCashVoucherStatus === "PAID" : Boolean(lpo.hasCheque);
  const steps = [
    { label: "Submitted", done: true, note: `${lpo.createdByName || ""} ${fmt(lpo.createdDate)}`.trim() },
    { label: "Approved", done: Boolean(lpo.approvedDate), note: lpo.approvedDate ? `${lpo.approvedByName || ""} ${fmt(lpo.approvedDate)}`.trim() : "" },
    { label: "Received", done: lpo.status === "FULLY_RECEIVED", partial: lpo.status === "PARTIALLY_RECEIVED", note: lpo.status === "PARTIALLY_RECEIVED" ? "partially" : lpo.status === "FULLY_RECEIVED" ? "fully" : "" },
    { label: "Invoice", done: Boolean(lpo.hasInvoice), note: lpo.hasInvoice ? `${lpo.invoiceRef || "attached"} ${fmt(lpo.invoiceDate)}`.trim() : "" },
    {
      label: isCash ? "Paid (petty cash)" : "Paid (cheque)",
      done: paid,
      partial: isCash && Boolean(lpo.pettyCashVoucherNumber) && !paid,
      note: isCash
        ? lpo.pettyCashVoucherNumber ? `${lpo.pettyCashVoucherNumber} · ${String(lpo.pettyCashVoucherStatus || "").toLowerCase()}` : ""
        : lpo.hasCheque ? `${lpo.chequeRef || ""} ${fmt(lpo.chequeDate)}`.trim() : "",
      to: isCash && lpo.pettyCashVoucherRecordGuid ? paths.pettyCashVoucherDetail(lpo.pettyCashVoucherRecordGuid) : null,
    },
  ];
  if (closed(lpo)) return null;
  return (
    <div className="card mb-3">
      <div className="card-body py-2">
        <div className="d-flex flex-wrap align-items-stretch" style={{ gap: 0 }}>
          {steps.map((s, i) => {
            const color = s.done ? "#1E7E34" : s.partial ? "#B7791F" : "#ADB5BD";
            return (
              <div key={s.label} className="d-flex align-items-center" style={{ flex: "1 1 0", minWidth: 150 }}>
                <div className="d-flex align-items-start gap-2 py-1 pe-2">
                  <span
                    className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                    style={{ width: 22, height: 22, background: s.done || s.partial ? color : "#fff", border: `2px solid ${color}`, color: "#fff", fontSize: 12, fontWeight: 700 }}
                  >
                    {s.done ? "✓" : s.partial ? "…" : ""}
                  </span>
                  <div style={{ lineHeight: 1.2 }}>
                    <div className="fw-semibold small" style={{ color: s.done || s.partial ? "#212529" : "#6c757d" }}>{s.label}</div>
                    <div className="text-muted" style={{ fontSize: 11 }}>
                      {s.to ? <Link to={s.to}>{s.note}</Link> : s.note || (s.done ? "" : "pending")}
                    </div>
                  </div>
                </div>
                {i < steps.length - 1 && <div className="flex-grow-1 me-2" style={{ height: 2, background: steps[i + 1].done || steps[i + 1].partial ? "#1E7E34" : "#E9ECEF", minWidth: 12 }} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
